import { useEffect, useState } from "react";
import { Button } from "@component/button";
import { Label } from "@component/label";
import { Checkbox } from "@component/checkbox";
import { useSnackbar } from "@hook/use-snackbar";
import { catchErrorLog } from "@util/sentry";
import {
    getTrainerEmailPreferences,
    updateTrainerEmailPreferences,
    TrainerEmailPreferences,
} from "@service/training";

// The three user-controllable Trainer notification emails (BE #3192 / PR #3199).
// `id` matches the BE preference key; copy mirrors the in-repo email-preferences
// preview spec.
const TRAINER_EMAIL_PREFERENCES: {
    id: keyof TrainerEmailPreferences;
    title: string;
    description: string;
}[] = [
    {
        id: "member_joined",
        title: "New member joined",
        description:
            "Get an email when someone you invited accepts and joins your training workspace.",
    },
    {
        id: "trainee_completed",
        title: "Trainee completed a roleplay",
        description:
            "Get an email when a trainee finishes a roleplay session in one of your campaigns.",
    },
    {
        id: "audio_upload",
        title: "Successful audio upload",
        description:
            "Get an email confirming when your call audio uploads finish processing for quality monitoring.",
    },
];

export default function EmailCommunicationTrainerSection() {
    const snackbar = useSnackbar();
    // Null until the backend responds — never seed server-backed state with
    // synthetic defaults, so a failed load can't be saved over the user's real
    // preferences.
    const [preferences, setPreferences] = useState<TrainerEmailPreferences | null>(null);
    const [savedPreferences, setSavedPreferences] = useState<TrainerEmailPreferences | null>(null);
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [isSaving, setIsSaving] = useState<boolean>(false);
    const [loadFailed, setLoadFailed] = useState<boolean>(false);

    useEffect(() => {
        getTrainerEmailPreferences()
            .then((prefs) => {
                setPreferences(prefs);
                setSavedPreferences(prefs);
                setLoadFailed(false);
            })
            .catch((error) => {
                setLoadFailed(true);
                snackbar.showError(
                    "Failed to load email preferences. Please refresh and try again.",
                );
                catchErrorLog(`failed to fetch trainer email preferences: ${error}`);
            })
            .finally(() => {
                setIsLoading(false);
            });
    }, []);

    const handleTogglePreference = (id: keyof TrainerEmailPreferences) => {
        setPreferences((prev) => (prev ? { ...prev, [id]: !prev[id] } : prev));
    };

    const hasChanges =
        !!preferences &&
        !!savedPreferences &&
        TRAINER_EMAIL_PREFERENCES.some(
            (pref) => preferences[pref.id] !== savedPreferences[pref.id],
        );

    const handleSavePreferences = () => {
        if (!preferences || loadFailed) return;
        setIsSaving(true);
        updateTrainerEmailPreferences(preferences)
            .then((updated) => {
                setPreferences(updated);
                setSavedPreferences(updated);
                snackbar.showSuccess("Email preferences saved.");
            })
            .catch((error) => {
                snackbar.showError("Failed to save email preferences. Please try again.");
                catchErrorLog(`failed to update trainer email preferences: ${error}`);
            })
            .finally(() => {
                setIsSaving(false);
            });
    };

    return (
        <div className="flex h-auto bg-white">
            {/* Left Panel - Title and Description */}
            <div className="flex-[0_0_25%] p-6" style={{ paddingTop: "32px", paddingBottom: "32px" }}>
                <div className="flex items-start space-x-3">
                    <div>
                        <h3 className="text-lg font-semibold text-gray-900 mb-2">
                            Email Notification Preferences
                        </h3>
                        <p className="text-sm text-gray-600">
                            Manage which types of emails you receive.
                        </p>
                    </div>
                </div>
            </div>

            {/* Right Panel - Checkbox List */}
            <div className="flex-[0_0_75%] p-6" style={{ paddingTop: "32px", paddingBottom: "32px" }}>
                <div className="space-y-6">
                    {TRAINER_EMAIL_PREFERENCES.map((preference) => (
                        <div key={preference.id} className="bg-white">
                            <div className="flex items-start space-x-3">
                                <Checkbox
                                    id={preference.id}
                                    checked={preferences?.[preference.id] ?? false}
                                    onCheckedChange={() => handleTogglePreference(preference.id)}
                                    disabled={isLoading || isSaving || !preferences || loadFailed}
                                    className="mt-1 data-[state=checked]:bg-blue-600 data-[state=checked]:border-blue-600"
                                />
                                <div className="flex-1 space-y-2">
                                    <div className="flex items-center space-x-2">
                                        <Label
                                            htmlFor={preference.id}
                                            className="text-sm font-semibold text-gray-900 cursor-pointer"
                                        >
                                            {preference.title}
                                        </Label>
                                    </div>
                                    <div className="space-y-1">
                                        <p className="text-sm text-gray-700">
                                            {preference.description}
                                        </p>
                                    </div>
                                </div>
                            </div>
                        </div>
                    ))}

                    <div className="flex justify-end pt-2">
                        <Button
                            onClick={handleSavePreferences}
                            disabled={isLoading || isSaving || !hasChanges || !preferences || loadFailed}
                            className="bg-blue-600 hover:bg-blue-700 text-white disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            {isSaving ? "Saving…" : "Save preferences"}
                        </Button>
                    </div>
                </div>
            </div>
        </div>
    );
}
